/**
 * Env — canonical geometry capabilities for card hover enlargement.
 *
 * The card hover system forks between two rendering strategies:
 *  - CSS `zoom` for physical enlargement
 *  - `transform` for a layout-neutral fallback
 *
 * The fork existed because legacy Safari's non-standard `zoom` didn't scale
 * absolutely-positioned children (card art, stats, hints are all abs-
 * positioned). Modern WebKit implements standard CSS zoom, so the decision is
 * now a ONE-SHOT runtime capability probe: an offscreen `zoom: 2` box with an
 * absolutely-positioned child. If the child's getBoundingClientRect reports
 * zoom-scaled geometry, standard `zoom` is usable — even on
 * Safari. If a real layout engine measures the box entirely UNzoomed, CSS
 * zoom is unsupported (older Firefox) and the functional transform path wins.
 * Only a genuinely inconclusive probe (jsdom's all-zero rects, a missing
 * layout engine, an exception) FAILS SAFE to the UA answer: non-Safari →
 * zoom, Safari → transform — exactly the historical behavior.
 *
 * This probe establishes geometry only; it is not evidence of pixel sharpness.
 * A separate margin probe records whether zoom scales flex-item margins so the
 * hand fan can preserve its exact footprint in either engine model.
 *
 * Load order: this script must run BEFORE cards.js (see index.html). Every
 * consumer keeps its old local UA fork as a fallback (`Env ? … :
 * <legacy expr>`) so a load-order hiccup can never break hover.
 */

let Env = {};

(function () {
    'use strict';

    // Single canonical Safari matcher. "safari" token present, but not a
    // Chromium/Android impostor (Chrome, Edge, and Android browsers all
    // carry "Safari/…" in their UA).
    const SAFARI_UA = /^((?!chrome|android).)*safari/i;

    function detectSafari() {
        try {
            return SAFARI_UA.test(navigator.userAgent || '');
        } catch (_) {
            return false;
        }
    }

    /**
     * Does CSS `zoom` scale absolutely-positioned children?
     * @returns {boolean|null} true  — standard CSS zoom (child scaled)
     *                         false — legacy WebKit zoom (child unscaled) OR
     *                                 zoom unsupported (host never zoomed)
     *                         null  — inconclusive (no layout engine / error)
     */
    function probeZoomScalesAbsChildren() {
        let host = null;
        try {
            if (typeof document === 'undefined' || typeof document.createElement !== 'function') return null;
            const mount = document.body || document.documentElement;
            if (!mount) return null;

            host = document.createElement('div');
            host.className = 'env-zoom-probe';
            host.setAttribute('aria-hidden', 'true');
            host.style.cssText = 'position:absolute;left:-9999px;top:-9999px;'
                + 'width:100px;height:100px;overflow:hidden;pointer-events:none;visibility:hidden;';
            host.style.zoom = '2';

            const child = document.createElement('div');
            child.className = 'env-zoom-probe-child';
            child.style.cssText = 'position:absolute;left:0;top:0;width:50px;height:50px;';
            host.appendChild(child);
            mount.appendChild(host);

            const hostRect = host.getBoundingClientRect();
            const childRect = child.getBoundingClientRect();
            const near = (value, expected) => Math.abs(value - expected) <= 1;

            if (!hostRect || !childRect) return null;
            // Host reports zoomed layout (100px × zoom 2): zoom participated.
            if (near(hostRect.width, 200)) {
                if (near(childRect.width, 100)) return true;  // child scaled → standard CSS zoom
                if (near(childRect.width, 50)) return false;  // child unscaled → legacy WebKit zoom
                return null;
            }
            // Host stayed at its unzoomed 100px while a REAL layout engine
            // measured the 50px child: CSS zoom is entirely unsupported (older
            // Firefox). Conclusive false — the transform path is functional;
            // the zoom path would be inert. jsdom's all-zero rects miss both
            // branches and stay inconclusive.
            if (near(hostRect.width, 100) && near(childRect.width, 50)) return false;
            return null;
        } catch (_) {
            return null;
        } finally {
            if (host && host.parentNode) host.parentNode.removeChild(host);
        }
    }

    function probeZoomScalesMargins() {
        let row = null;
        try {
            if (typeof document === 'undefined' || typeof document.createElement !== 'function') return null;
            const mount = document.body || document.documentElement;
            if (!mount) return null;

            row = document.createElement('div');
            row.className = 'env-zoom-margin-probe';
            row.setAttribute('aria-hidden', 'true');
            row.style.cssText = 'position:absolute;left:-9999px;top:-9999px;display:flex;'
                + 'width:max-content;pointer-events:none;visibility:hidden;';

            const target = document.createElement('div');
            target.className = 'env-zoom-margin-target';
            target.style.cssText = 'width:100px;height:1px;margin:0 10px;flex:none;';
            target.style.zoom = '2';

            const marker = document.createElement('div');
            marker.className = 'env-zoom-margin-marker';
            marker.style.cssText = 'width:1px;height:1px;flex:none;';
            row.append(target, marker);
            mount.appendChild(row);

            const rowRect = row.getBoundingClientRect();
            const targetRect = target.getBoundingClientRect();
            const markerRect = marker.getBoundingClientRect();
            if (!rowRect || !targetRect || !markerRect) return null;

            const near = (value, expected) => Math.abs(value - expected) <= 1;
            if (!near(targetRect.width, 200)) return null;
            const targetLeft = targetRect.left - rowRect.left;
            const markerLeft = markerRect.left - rowRect.left;
            if (near(targetLeft, 20) && near(markerLeft, 240)) return true;
            if (near(targetLeft, 10) && near(markerLeft, 220)) return false;
            return null;
        } catch (_) {
            return null;
        } finally {
            row?.remove();
        }
    }

    const isSafari = detectSafari();
    const zoomProbe = probeZoomScalesAbsChildren();
    // A conclusive probe wins (modern WebKit graduates to standard zoom);
    // an inconclusive one fails safe to the historical UA fork.
    const useZoomHover = zoomProbe === null ? !isSafari : zoomProbe;
    const zoomScalesMargins = zoomProbe === true ? probeZoomScalesMargins() : null;

    Env = Object.freeze({ isSafari, useZoomHover, zoomProbe, zoomScalesMargins });
})();

export { Env };
